import { useMemo } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import { groupEntries } from './entryGroupFilter';
import { formatDurationSeconds } from './timeEntries';
import TrackerEntryRow, { type TrackerRowProjectOption } from './TrackerEntryRow';

export interface KronosHistoryProps {
  entries: TimeEntry[];
  projectOptions: TrackerRowProjectOption[];
  projectLabelById: ReadonlyMap<string, string>;
  designationLabelsById: ReadonlyMap<string, string>;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  onLoadMore: () => void;
  onUpdate: (entryId: string, patch: Record<string, unknown>) => Promise<void>;
  onDuplicate: (entry: TimeEntry) => void;
  onDelete: (entry: TimeEntry) => void;
  onContinueTimer: (entry: TimeEntry) => void;
  formatWeekLabel: (weekKey: string) => string;
}

/**
 * Multi-week history below the current week. Groups one-shot-loaded
 * past entries by ISO week (newest first) and renders the same
 * inline-editable `TrackerEntryRow` as the week recap. The history feed
 * is not real-time, so the parent hides deleted rows via its own
 * removed-id filter before passing `entries` here.
 */
export default function KronosHistory({
  entries,
  projectOptions,
  projectLabelById,
  designationLabelsById,
  hasMore,
  loading,
  loadingMore,
  error,
  onLoadMore,
  onUpdate,
  onDuplicate,
  onDelete,
  onContinueTimer,
  formatWeekLabel,
}: KronosHistoryProps) {
  const { t } = useTranslation();

  const weekGroups = useMemo(
    () =>
      groupEntries(entries, 'week', {
        userLabelsById: new Map(),
        designationLabelsById,
      }),
    [entries, designationLabelsById],
  );

  return (
    <Box sx={{ mt: 4 }} data-testid="kronos-history">
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('kronos.entries.historyTitle')}
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="history-error">
          {t('kronos.entries.historyError')}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderColor: colors.lightGray, borderRadius: 0 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }} data-testid="history-loading">
            <CircularProgress size={24} />
          </Box>
        ) : weekGroups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }} data-testid="history-empty">
            <Typography variant="body2" color="text.secondary">
              {t('kronos.entries.historyEmpty')}
            </Typography>
          </Box>
        ) : (
          weekGroups.map((group) => (
            <Box key={group.key} data-testid={`history-week-${group.key}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(0,0,0,0.03)',
                  borderTop: `1px solid ${colors.lightGray}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {formatWeekLabel(group.key)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatDurationSeconds(group.totalSeconds)}
                </Typography>
              </Box>
              {group.entries.map((data) => (
                <TrackerEntryRow
                  key={data.id}
                  entry={data}
                  projectOptions={projectOptions}
                  projectLabel={projectLabelById.get(data.projectId) ?? data.projectId}
                  designationLabel={designationLabelsById.get(data.designationId) ?? data.designationId}
                  onUpdate={(patch) => onUpdate(data.id, patch)}
                  onDuplicate={() => onDuplicate(data)}
                  onDelete={() => onDelete(data)}
                  onContinueTimer={() => onContinueTimer(data)}
                />
              ))}
            </Box>
          ))
        )}
      </Paper>

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid="history-load-more"
          >
            {loadingMore ? <CircularProgress size={20} /> : t('kronos.entries.historyShowMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
